/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import isNull from 'lodash.isnull';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {acknowledgeAlerts} from '@ion/core/src/services/Alerts';
import {Device, GenericObject} from '@ion/core/src/types/entities';
import {
  getCurrentDate,
  timeDiff
} from '@ion/core/src/utils/DateUtil';
import {CTALink} from '@ion/dls/src/components/buttons/CTALink';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {CellMenu} from '@ion/dls/src/components/grid/CellMenu';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import DatePicker from '@ion/dls/src/editors/datetimepicker/DatePicker';
import {ColumnChooserIcon} from '@ion/dls/src/icons/ColumnChooserIcon';
import {GridRefreshIcon} from '@ion/dls/src/icons/GridRefreshIcon';
import IonSnackbar from '@ion/dls/src/notifications/IonSnackbar';
import {
  dlsAlt,
  dlsColor,
  dlsTypography
} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {
  datePickerDateValidations,
  getInvalidDateErrorMessages
} from '@ion/features/src/search/utils';
import AlertHistoryGrid from './AlertHistoryGrid';
import {getAlertTime} from './helpers';

export interface AlertHistoryTabProps {
  selectedTab: Number;
  tabIndex: Number;
  device?: Device;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  formSection : {
    ...dlsAlt('typography', 'cta3'),
    fontWeight    : 700,
    color         : dlsColor('grey1'),
    letterSpacing : '0',
    padding       : '20px 0 10px 0'
  },
  flexStyle : {
    display                        : 'flex',
    marginTop                      : '20px',
    alignItems                     : 'center',
    [theme.breakpoints.down('sm')] : {
      flexWrap : 'wrap'
    }
  },
  dropdownStyle : {
    width                          : '160px',
    marginRight                    : '10px',
    [theme.breakpoints.down('xs')] : {
      width       : '48%',
      marginRight : '5px',
      marginTop   : '15px'
    }
  },
  searchContainer : {
    marginRight                    : '10px',
    marginTop                      : '26px',
    [theme.breakpoints.down('xs')] : {
      marginTop    : '10px',
      marginBottom : '10px'
    }
  },
  selectStyles : {
    width       : '180px',
    marginRight : '10px'
  },
  filterStyles : {
    width       : '180px',
    marginRight : '0px'
  },
  ackLink : {
    marginRight : '10px',
    marginTop   : '30px'
  },
  ctaLink : {
    ...dlsTypography('cta'),
    color         : dlsColor('aaBlue'),
    cursor        : 'pointer',
    fontSize      : '14px',
    letterSpacing : '1px'
  },
  disabled : {
    pointerEvents : 'none',
    color         : dlsColor('grey4')
  },
  iconStyle : {
    display                        : 'flex',
    marginLeft                     : 'auto',
    [theme.breakpoints.down('sm')] : {
      marginLeft : 0
    }
  },
  refreshStyle : {
    marginTop : '35px'
  },
  headerItem : {
    ...dlsAlt('typography', 'label3'),
    fontWeight    : 700,
    color         : dlsColor('grey1'),
    padding       : '5px 5px 5px 10px',
    textTransform : 'capitalize'
  },
  bodyItem : {
    ...dlsAlt('typography', 'label2'),
    color : dlsColor('aaBlue')
  },
  buttonStyle : {
    marginTop : '25px'
  },
  buttonGroup : {
    height : '30px'
  },
  gridContainer : {
    margin : '20px 10px 20px 0',
    height : 'calc(100vh - 434px)'
  },
  detailsTab : {
    height : '100%'
  }
}));

/**
 * AlertHistoryTab component that displays the Device
 * Alerts data for the selected Device Details tab.
 */
export const AlertHistoryTab = ({
  device,
  tabIndex = 0,
  selectedTab = 0
}: AlertHistoryTabProps) => {
  const classes                             = useStyles();
  const gridRef                             = useRef<GenericObject>();
  const [timeline, setTimeline]             = useState<string>('recent');
  const [startDate, setStartDate]           = useState(null);
  const [endDate, setEndDate]               = useState(null);
  const [alertTime, setAlertTime]           = useState(null);
  const [alertType, setAlertType]           = useState<string>('all');
  const [isValidDates, setIsValidDates]     = useState<boolean>(true);
  const [disableBtn, setDisableBtn]         = useState<boolean>(true);
  const [refreshGrid, setRefreshGrid]       = useState<boolean>(false);
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
  const [showErrorSnack, setShowErrorSnack] = useState<boolean>(false);
  const [totalRows, setTotalRows]           = useState(0);
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [errorType, setErrorType]           = useState<string>('');
  const cellMenuRef                         = useRef(null);

  const closeMenu = function() {
    if (cellMenuRef.current) {
      cellMenuRef.current.closeCellMenu(cellMenuRef.current, true);
    }
  };

  const isRecentAlerts = timeline === 'recent';

  const alertHistoryOptions = useMemo(() => ([
    {value: 'all', label: translate('admin-devices:view.alertHistory.all')},
    {value: 'acknowledged', label: translate('admin-devices:view.alertHistory.acknowledged')},
    {value: 'unAcknowledged', label: translate('admin-devices:view.alertHistory.unAcknowledged')}
  ]), []);

  const timelineOptions = useMemo(() => ([
    {value: 'recent', label: translate('admin-devices:view.alertHistory.recentAlerts')},
    {value: 'custom', label: translate('admin-devices:view.alertHistory.customDates')}
  ]), []);

  const toggleDatePicker = useCallback(function() {
    setOpenDatePicker(true);
    setTimeout(() => {
      setOpenDatePicker(false);
    }, 1000);
  }, []);

  const refreshData = useCallback(function() {
    if (gridRef.current) {
      gridRef.current?.onRefresh(true);
    }
    closeMenu();
  }, [device]);

  const onSearch = useCallback(function() {
    if (!isValidDates) {
      setShowErrorSnack(true);
    } else {
      refreshData();
    };
  }, [isValidDates, alertTime]);

  const onCloseSnack = useCallback(() => {
    setShowErrorSnack(false);
  }, []);

  const onAcknowledgeClick = useCallback(() => {
    const doAcknowledge = async function() {
      try {
        acknowledgeAlerts(selectedAlerts.map(({id}) => id) as number[])
          .then(function() {
            refreshData();
          });
      } catch (err) {
        error('Error occurred acknowlege alerts.');
      }
    };

    doAcknowledge();
  }, [selectedAlerts]);

  useEffect(() => {
    refreshData();
  }, [timeline, alertType, device]);

  useEffect(() => {
    setShowErrorSnack(false);
    setErrorType('');
    const {isValid, errorType} = datePickerDateValidations(startDate, endDate);
    setIsValidDates(isValid);
    setErrorType(errorType);
    if (isValid) {
      setAlertTime(getAlertTime(startDate, endDate));
    }
  }, [startDate, endDate]);

  useEffect(() => {
    let isAcknowledged = false;
    if (selectedAlerts?.length > 0) {
      selectedAlerts?.forEach((row) => {
        if (!isNull(row?.acknowledgedById)) {
          isAcknowledged = true;
        }
      });
    } else {
      isAcknowledged = true;
    }
    setDisableBtn(isAcknowledged);
  }, [selectedAlerts]);

  const onShowColumnChooser = useCallback((val) =>
    function() {
      gridRef.current?.toggleColumnChooser?.(val);
      closeMenu();
    }
  , []);

  return (
    <div id={`detailsTab_${tabIndex}`} className={classes.detailsTab} hidden={selectedTab !== tabIndex}>
      <div className={classes.flexStyle}>
        <div className={classes.selectStyles}>
          <Dropdown displaySelectedLabel
            options={timelineOptions}
            fieldLabel={translate('admin-devices:view.alertHistory.timeline')}
            value={timeline}
            name={'timeline'}
            onChange={function(event, {value}) {
              setTimeline(value);
            }} />
        </div>
        <div className={classes.dropdownStyle}>
          <DatePicker closeOnClickOutside
            useDatePickerTextField
            disableFuture
            disabled={isRecentAlerts}
            open={openDatePicker}
            hasError={showErrorSnack}
            fieldLabel={translate('admin-devices:view.alertHistory.startDate')}
            toggle={toggleDatePicker}
            defaultValue={getCurrentDate()}
            onChange={function(value) {
              setStartDate(value);
            }}/>
        </div>
        <div className={classes.dropdownStyle}>
          <DatePicker closeOnClickOutside
            useDatePickerTextField
            disableFuture
            disabled={isRecentAlerts}
            open={openDatePicker}
            hasError={showErrorSnack}
            fieldLabel={translate('admin-devices:view.alertHistory.endDate')}
            toggle={toggleDatePicker}
            defaultValue={getCurrentDate()}
            onChange={function(value) {
              setEndDate(value);
            }}/>
        </div>
        <div className={classes.searchContainer}>
          <PrimaryButton onClick={onSearch} disabled={isRecentAlerts}>
            {translate('admin-devices:view.alertHistory.searchBtn')}
          </PrimaryButton>
        </div>
        {totalRows > 0 && (
          <div className={classes.ackLink}>
            <CTALink className={classnames(classes.ctaLink, disableBtn ? classes.disabled : '')}
              onClick={onAcknowledgeClick}>
              {translate('admin-devices:view.alertHistory.acknowledgeBtn')}
            </CTALink>
          </div>
        )}
        <div className={classes.iconStyle}>
          <div className={classes.filterStyles}>
            <Dropdown displaySelectedLabel
              options={alertHistoryOptions}
              fieldLabel={translate('admin-devices:view.alertHistory.filterAlertHistory')}
              value={alertType}
              name={'alertType'}
              disabled={isRecentAlerts}
              onChange={function(event, {value}) {
                setAlertType(value);
              }} />
          </div>
          <div className={classes.refreshStyle}>
            <CellMenu ref={cellMenuRef} menuItems={
              <div>
                <CellMenuItem icon={<ColumnChooserIcon />}
                  onClick={onShowColumnChooser(true)}
                  label={translate('common:columnChooser.title')} />
                <CellMenuItem icon={<GridRefreshIcon />}
                  onClick={refreshData}
                  label={translate('common:refresh')} />
              </div>
            } />
          </div>
        </div>
      </div>
      <div className={classes.gridContainer}>
        <AlertHistoryGrid device={device}
          ref={gridRef}
          refreshGrid={refreshGrid}
          isRecentAlerts={isRecentAlerts}
          alertTime={alertTime}
          alertType={alertType}
          setTotalRows={setTotalRows}
          setSelectedAlerts={setSelectedAlerts} />
      </div>
      {showErrorSnack && (
        <IonSnackbar show
          customDismissLabel={translate('common:dismiss')}
          message={getInvalidDateErrorMessages(errorType)}
          onDismiss={onCloseSnack} />
      )}
    </div>
  );
};

export default AlertHistoryTab;
